import { useSession } from "@/contexts/Auth";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { useRBAC } from "@/hooks/useRBAC";
import { useTrialNow } from "@/hooks/useTrialNow";
import { FEATURE_FLAGS } from "@/lib/featureFlags";
import {
  getTrialLifecycleFromDates,
  type TrialLifecycle,
} from "@/lib/trial-status";

/**
 * Which surface is asking, which decides what makes an organization eligible.
 *
 * - `"active-trial"`: the in-app surfaces (billing page, sidebar trial card),
 *   where checkout converts a running trial.
 * - `"gated"`: the dashboard lockout pages, where checkout is the way out of
 *   the gate for an organization that is walled off entirely.
 * - `"unsubscribed-base"`: billing has confirmed no Stripe subscription for a
 *   Free/Base organization; existing paid and contracted plans are excluded.
 * - `"unsubscribed-payg"`: billing has confirmed there is no Stripe subscription,
 *   so a PAYG organization can finish or restart checkout without an active trial.
 */
export type PaygCheckoutEligibility =
  | "active-trial"
  | "gated"
  | "unsubscribed-payg"
  | "unsubscribed-base";

/**
 * Whether this viewer may start a self-serve checkout, plus the organization
 * the single-flight lock belongs to and the trial lifecycle the answer was
 * derived from — returned so callers that also branch on the trial read the
 * same clock instead of deriving their own.
 *
 * Reads the session from context on every surface. `AuthProvider` returns the
 * lockout pages above `SessionContext.Provider`, so a gated caller supplies the
 * session itself — see `LockoutPaygCheckoutPanel`.
 */
export function usePaygCheckoutAccess(eligibility: PaygCheckoutEligibility): {
  eligible: boolean;
  activeOrganizationId: string;
  trialLifecycle: TrialLifecycle;
} {
  const flag = useFeatureFlag(FEATURE_FLAGS.paygSelfServeBilling);
  const { hasScope } = useRBAC();
  const {
    trial,
    activeOrganizationId,
    whitelisted,
    rawGramAccountType,
    hasActiveSubscription,
  } = useSession();
  // A trial that ends while the page is open has to take the CTA with it, so
  // the lifecycle below reads a clock that re-renders on the trial's own
  // boundaries instead of whenever a parent happens to re-render.
  const now = useTrialNow(trial);
  const trialLifecycle = getTrialLifecycleFromDates(trial, now);

  let eligibleForSurface: boolean;
  switch (eligibility) {
    case "active-trial":
      eligibleForSurface = trialLifecycle === "active";
      break;
    case "gated":
      eligibleForSurface = !whitelisted;
      break;
    case "unsubscribed-base":
      eligibleForSurface =
        (rawGramAccountType === "free" || rawGramAccountType === "base") &&
        !hasActiveSubscription;
      break;
    case "unsubscribed-payg":
      eligibleForSurface = rawGramAccountType === "payg";
      break;
  }

  return {
    eligible:
      flag.status === "enabled" && hasScope("org:admin") && eligibleForSurface,
    activeOrganizationId,
    trialLifecycle,
  };
}
